<template>
  <div :class="[$style['gallery'], images.length > 1 && $style['gallery--multiple']]" role="group" :aria-label="`Foto's van ${title}`">
    <div :class="$style['gallery__stage']">
      <div
        v-if="images.length"
        ref="track"
        :class="[$style['gallery__track'], images.length % 2 && $style['gallery__track--odd']]"
        @scroll.passive="onScroll"
        @keydown.left.prevent="go(index - 1)"
        @keydown.right.prevent="go(index + 1)">
        <button
          v-for="(id, i) in images"
          :key="id"
          ref="slides"
          type="button"
          :tabindex="!carousel || i === index ? 0 : -1"
          :class="$style['gallery__slide']"
          @click="viewer = i">
          <!-- The large first photo gets the sharper size on a larger screen -->
          <picture>
            <source
              v-if="i === 0 && images.length % 2"
              media="(width > 50rem)"
              :srcset="`${url('full', id)} 576w, ${url('hd', id)} 1152w`"
              sizes="40rem">
            <img
              :src="url('full', id)"
              :alt="alt(i)"
              :loading="i ? 'lazy' : 'eager'"
              :fetchpriority="i ? undefined : 'high'"
              :class="$style['gallery__image']">
          </picture>
          <span :class="$style['gallery__zoom']" aria-hidden="true"><ZoomIn /></span>
        </button>
      </div>
      <!-- No photo yet: the logo, quietly, so the frame doesn't look broken -->
      <div v-else :class="$style['gallery__empty']">
        <SiteMark :class="$style['gallery__placeholder']" />
      </div>
    </div>

    <!-- Phones: a thin bar per photo under it shows where you are -->
    <div v-if="images.length > 1" :class="$style['gallery__bars']">
      <button
        v-for="(id, i) in images"
        :key="id"
        type="button"
        :aria-label="alt(i)"
        :aria-current="i === index || undefined"
        :class="$style['gallery__bar']"
        @click="go(i)" />
    </div>

    <Lightbox
      v-if="viewer !== null"
      :images="images.map((id, i) => ({ src: url('hd', id), alt: alt(i) }))"
      :start="viewer"
      :label="`Foto's van ${title}`"
      @close="onViewerClose" />
  </div>
</template>

<script setup lang="ts">
// A product's photos. Larger screens: all of them large, one under the other (an odd number starts
// with one across the full width), next to the text that stays in view. Phones: swipe through them.
// A click or tap opens them over the whole screen, in the largest size.
import { ZoomIn } from 'lucide-vue-next'

const props = defineProps<{
  images: string[]
  title: string
}>()

const config = useRuntimeConfig()
const track = ref<HTMLElement>()
const slides = ref<HTMLButtonElement[]>([])
const index = ref(0)
const viewer = ref<number | null>(null)
// Phones swipe through the photos one at a time
const carousel = ref(false)
let query: MediaQueryList | undefined
let frame = 0

// The width of one photo in the swipe track
const step = () => (track.value?.firstElementChild as HTMLElement | null)?.offsetWidth || track.value?.clientWidth || 1

const url = (size: 'thumbs' | 'full' | 'hd', id: string) => `${config.public.cdnUrl}/products/${size}/${id}.webp`
const alt = (i: number) => props.images.length > 1
  ? `${props.title}, foto ${i + 1} van ${props.images.length}`
  : props.title

function go (to: number, smooth = true) {
  if (!track.value || to < 0 || to >= props.images.length) { return }
  index.value = to
  const motion = smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  track.value.scrollTo({ left: to * step(), behavior: motion ? 'smooth' : 'instant' })
}

function onScroll () {
  if (!carousel.value) { return }
  cancelAnimationFrame(frame)
  frame = requestAnimationFrame(() => {
    if (!track.value) { return }
    // At the end the last photo can't scroll to the start, so the end counts as the last one
    const end = track.value.scrollLeft + track.value.clientWidth >= track.value.scrollWidth - 1
    index.value = end ? props.images.length - 1 : Math.round(track.value.scrollLeft / step())
  })
}

// Back on the page at the photo you looked at last
function onViewerClose (last: number) {
  viewer.value = null
  if (carousel.value) {
    go(last, false)
    slides.value[last]?.focus({ preventScroll: true })
  } else {
    slides.value[last]?.focus()
  }
}

const onQuery = () => { carousel.value = !!query?.matches }

onMounted(() => {
  query = window.matchMedia('(width <= 50rem)')
  onQuery()
  query.addEventListener('change', onQuery)
})

// A new product in the same page: start at its first photo
watch(() => props.images, () => {
  index.value = 0
  if (track.value) { track.value.scrollLeft = 0 }
})

onBeforeUnmount(() => {
  cancelAnimationFrame(frame)
  query?.removeEventListener('change', onQuery)
})
</script>

<style module>
.gallery {
  min-width: 0;
}

.gallery__stage {
  position: relative;
  isolation: isolate;
}

/* Larger screens: the photos one under the other, two side by side after an odd first one */
.gallery__track {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--spacing-m);
}

.gallery__track--odd > :first-child {
  grid-column: 1 / -1;
}

.gallery__slide {
  position: relative;
  display: block;
  aspect-ratio: 9 / 16;
  padding: 0;
  overflow: hidden;
  border: 0;
  background: var(--background-raised);
  cursor: zoom-in;
}

.gallery__slide:focus-visible {
  outline: 0.125rem solid var(--text-base);
  outline-offset: -0.25rem;
}

.gallery__slide picture,
.gallery__image {
  display: block;
  width: 100%;
  height: 100%;
}

.gallery__image {
  object-fit: cover;
}

/* Says the photo opens larger, once you point at it */
.gallery__zoom {
  position: absolute;
  right: var(--spacing-l);
  bottom: var(--spacing-l);
  display: grid;
  place-items: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  background: oklch(0% 0 0deg / 60%);
  color: var(--stage-white);
  opacity: 0;
  transition: opacity 200ms;
  pointer-events: none;
}

.gallery__slide:hover .gallery__zoom,
.gallery__slide:focus-visible .gallery__zoom {
  opacity: 1;
}

.gallery__zoom svg {
  width: 1.25rem;
  height: 1.25rem;
}

.gallery__empty {
  display: grid;
  place-items: center;
  aspect-ratio: 9 / 16;
  background: var(--background-raised);
}

.gallery__placeholder {
  font-size: 5rem;
  opacity: 0.18;
}

.gallery__bars {
  display: none;
}

/* A phone: each photo across the full width, whole; swipe through, a thin bar per photo shows where you are */
@media (width <= 50rem) {
  .gallery {
    display: grid;
    gap: var(--spacing-m);
    margin-inline: calc(-1 * var(--container-gutter, var(--spacing-xl)));
  }

  .gallery__stage {
    overflow: hidden;
    aspect-ratio: 9 / 16;
    background: var(--background-raised);
  }

  .gallery__track {
    display: flex;
    height: 100%;
    gap: 0;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
  }

  .gallery__track::-webkit-scrollbar {
    display: none;
  }

  .gallery__slide {
    flex: 0 0 100%;
    height: 100%;
    aspect-ratio: auto;
    background: none;
    scroll-snap-align: start;
    scroll-snap-stop: always;
  }

  /* A tap opens it; the icon would sit on the next photo */
  .gallery__zoom {
    display: none;
  }

  .gallery__empty {
    height: 100%;
    aspect-ratio: auto;
  }

  .gallery__bars {
    display: flex;
    justify-content: center;
    gap: var(--spacing-s);
  }

  .gallery__bar {
    position: relative;
    flex: 0 1 2rem;
    height: 1.5rem;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
  }

  .gallery__bar::before {
    content: "";
    position: absolute;
    inset: calc(50% - 0.0625rem) 0 auto;
    height: 0.125rem;
    background: var(--stage-line);
    transition: background-color 200ms;
  }

  .gallery__bar[aria-current]::before {
    background: var(--stage-white);
  }
}

@media (prefers-reduced-motion: reduce) {
  .gallery__zoom {
    transition: none;
  }
}
</style>
